import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ResolveFn, Router } from '@angular/router';
import { CampaignService } from '@core/services/api/campaign.service';
import { StepService } from '@core/services/api/step.service';
import { TaskService } from '@core/services/api/task.service';
import { IEBCampaign } from '@shared/models/entities/campaign.interface';
import { IEBAction } from '@shared/models/entities/action.interface';
import { IEBTask } from '@shared/models/entities/task.interface';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface CampaignBreadcrumbContext {
  campaign: IEBCampaign;
}

export interface TaskMgmtBreadcrumbContext {
  campaign: IEBCampaign;
  step: IEBAction;
}

export interface TaskBreadcrumbContext {
  campaign: IEBCampaign;
  step: IEBAction;
  task: IEBTask;
}

function getNavigationState<T>(key: string): T | undefined {
  const router = inject(Router);
  const nav = router.getCurrentNavigation();
  const state = nav?.extras.state as Record<string, unknown> | undefined;
  return state?.[key] as T | undefined;
}

/**
 * Map breadcrumb-resolver errors to `of(null)` but distinguish
 * between expected Not-Found (silently null) and unexpected errors
 * (logged for observability). Auth errors are handed to the
 * session-expired interceptor upstream.
 */
function handleResolverError(label: string) {
  return (error: unknown) => {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
        return of(null);
      }
      if (error.status === 401 || error.status === 403) {
        console.warn(`[breadcrumb:${label}] auth error`, error.status);
        return of(null);
      }
    }
    console.error(`[breadcrumb:${label}] failed`, error);
    return of(null);
  };
}

export const campaignBreadcrumbContextResolver: ResolveFn<
  CampaignBreadcrumbContext | null
> = (route): Observable<CampaignBreadcrumbContext | null> => {
  const service = inject(CampaignService);
  const stateCampaign = getNavigationState<IEBCampaign>('campaign');
  const campaignId = route.paramMap.get('id') ?? route.paramMap.get('campaignId');

  if (!campaignId) {
    return of(null);
  }

  if (stateCampaign?.campaignId === campaignId) {
    return of({ campaign: stateCampaign });
  }

  return service.findOne(campaignId).pipe(
    map((campaign) => ({ campaign })),
    catchError(handleResolverError('campaign')),
  );
};

export const taskMgmtBreadcrumbContextResolver: ResolveFn<
  TaskMgmtBreadcrumbContext | null
> = (route): Observable<TaskMgmtBreadcrumbContext | null> => {
  const stepService = inject(StepService);
  const campaignService = inject(CampaignService);

  const stateStep = getNavigationState<IEBAction>('step');
  const stepId = route.paramMap.get('id') ?? route.paramMap.get('stepId');

  if (!stepId) {
    return of(null);
  }

  const step$ =
    stateStep?.actionId === stepId ? of(stateStep) : stepService.findOne(stepId);

  return step$.pipe(
    switchMap((step) => {
      const campaignId = step.campaign?.campaignId ?? step.campaignId ?? null;
      if (!campaignId) {
        return of(null);
      }

      const campaign$ =
        step.campaign?.campaignId === campaignId
          ? of(step.campaign)
          : campaignService.findOne(campaignId);

      return campaign$.pipe(
        map((campaign) => ({ campaign, step })),
        catchError(handleResolverError('taskMgmt.campaign')),
      );
    }),
    catchError(handleResolverError('taskMgmt.step')),
  );
};

export const taskBreadcrumbContextResolver: ResolveFn<
  TaskBreadcrumbContext | null
> = (route): Observable<TaskBreadcrumbContext | null> => {
  const taskService = inject(TaskService);
  const campaignService = inject(CampaignService);

  const stateTask = getNavigationState<IEBTask>('task');
  const taskId = route.paramMap.get('id') ?? route.paramMap.get('taskId');

  if (!taskId) {
    return of(null);
  }

  const task$ =
    stateTask?.taskId === taskId ? of(stateTask) : taskService.findOne(taskId);

  return task$.pipe(
    switchMap((task) => {
      const step = task.action;
      if (!step) {
        return of(null);
      }
      const campaignId = step.campaign?.campaignId ?? step.campaignId ?? null;
      if (!campaignId) {
        return of(null);
      }

      const campaign$ =
        step.campaign?.campaignId === campaignId
          ? of(step.campaign)
          : campaignService.findOne(campaignId);

      return campaign$.pipe(
        map((campaign) => ({ campaign, step, task })),
        catchError(handleResolverError('task.campaign')),
      );
    }),
    catchError(handleResolverError('task')),
  );
};

